import { scaleThreshold, ScaleThreshold } from 'd3-scale';

import { normalizeColoredRanges } from './normalize-colored-ranges.js';
import { ColoredRange } from '../types/colored-range.js';
import type { MinMax } from '../types/min-max.js';

/**
 * Sorts ranges first by starting point then by ending point of the range
 *
 * @param ranges -
 */
export const sortRanges = (ranges: ColoredRange[]): ColoredRange[] => {
  return [...ranges].sort((a, b) => {
    if (a.from === b.from) {
      return a.to - b.to;
    }
    return a.from - b.from;
  });
};

/**
 * Builds a threshold scale based on a list of threshold ranges
 *
 * @internal
 *
 * @param ranges -
 * @param defaultColor - default color for ranges
 * @param minMaxConfig -
 */
export const buildThresholdScaleFromRanges = (
  ranges: ColoredRange[],
  defaultColor: string,
  minMaxConfig?: Partial<MinMax>,
): ScaleThreshold<number, string> => {
  const sortedRanges = sortRanges(ranges);
  const boundedRanges = boundRanges(sortedRanges, minMaxConfig);

  const normalizedRanges = normalizeColoredRanges(boundedRanges, defaultColor);

  const steps = normalizedRanges.map((value) => value.from);
  const lastStep = normalizedRanges[normalizedRanges.length - 1].to;
  steps.push(lastStep);

  const colors = normalizedRanges.map((value) => value.color);

  const colorRange = [defaultColor, ...colors];
  if (minMaxConfig?.max && minMaxConfig.max === lastStep) {
    colorRange.push(colors[colors.length - 1]);
  } else {
    colorRange.push(defaultColor);
  }

  return scaleThreshold<number, string>()
    .domain(new Set(steps))
    .range(colorRange);
};

/**
 * Builds ranges bounded by the min/max config.
 *
 * @internal
 *
 * @param ranges -
 * @param minMaxConfig -
 */
export const boundRanges = (
  ranges: ColoredRange[],
  minMaxConfig?: Partial<MinMax>,
) => {
  const boundedRanges: ColoredRange[] = [];
  const min = minMaxConfig?.min ?? ranges[0].from;
  const max = minMaxConfig?.max ?? ranges[ranges.length - 1].to;

  for (const range of ranges) {
    if (
      (range.from < min && range.to > min) ||
      (range.from >= min && range.from <= max)
    ) {
      const newRange = { ...range };
      newRange.from = Math.max(range.from, min);
      newRange.to = Math.min(range.to, max);
      boundedRanges.push(newRange);
    }
  }

  return boundedRanges;
};
